import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Grid, TextField, InputAdornment, IconButton, CircularProgress, Tooltip } from '@material-ui/core';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import InfoIcon from '@material-ui/icons/Info';
import { HIDE_FORCE_CHANGE_PASSWORD_MODAL } from './actions';
import { resetPasswordExpiredToast } from '../utils/snackbarUtils';
import { globalPostService } from '../utils/globalApiServices';
import { checkApiStatus } from '../utils';
import { regexConstants } from '../constants/regEx';
import { authorizeAutoLogin } from '../utils';

const ChangePasswordPopUp = ({ open, onClose, responseData }) => {
	const [showChangePasswordForm, setShowChangePasswordForm] = useState(false);
	const { enqueueSnackbar } = useSnackbar();
	const [isLoading, setLoading] = useState(false);
	const [showPassword, setShowPassword] = useState({ password_1: false, password_2: false, password_3: false });
	const [userInfo, setUserInfo] = useState({ old_password: '', password: '', confirm_password: '', accessToken: '' });
	const [error, setError] = useState({});

	useEffect(() => {
		if (open) {
			setShowChangePasswordForm(false);
		}
	}, [open]);

	const onFieldChange = (keyParam, value) => {
		setUserInfo({ ...userInfo, [keyParam]: value, accessToken: responseData?.data?.access });
	}

	const resetState = () => {
		setUserInfo({ old_password: '', password: '', confirm_password: '' });
		setError({});
		setShowPassword({ password_1: false, password_2: false, password_3: false });
	}

	const handleClose = () => {
		if (isLoading) return;
		resetState();
		onClose && onClose();
	}

	const onChangePwd = () => {
		let validationInputs = {};
		validationInputs = {
			old_password: userInfo?.old_password?.trim().length ? regexConstants.password.test(userInfo.old_password) ? '' : 'Please enter valid Old Password' : 'Old Password is required',
			password: userInfo?.password?.trim().length ? regexConstants.password.test(userInfo.password) ? '' : 'Please enter valid New Password' : 'New Password is required',
			confirm_password: userInfo?.confirm_password?.trim().length ? userInfo.confirm_password === userInfo.password ? '' : 'Confirm Password should be same as New Password' : 'Confirm Password is required',
		}
		if (Object.keys(validationInputs).every((k) => { return validationInputs[k] === '' })) {
			setLoading(true);
			globalPostService('api/password-change/', userInfo)
				.then(response => {
					if (checkApiStatus(response)) {
						enqueueSnackbar(response.data.message, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
						resetState();
						onClose && onClose();
						authorizeAutoLogin({ lessor_id: (responseData?.data?.current_lessor?.id || responseData?.data?.lessor_id) })
						setLoading(false);
					} else {
						enqueueSnackbar(response.data.message, { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
						setLoading(false);
					}
				})
		} else {
			setError(validationInputs);
		}
	}

	if (open && !showChangePasswordForm) {
		return (
			<Dialog open={open} maxWidth='xs' fullWidth className='password-info-modal'>
				<DialogContent>
					<Typography>Your password has expired. Please change your password</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setShowChangePasswordForm(true)} color='primary' variant='contained'>
						Change Password
					</Button>
				</DialogActions>
			</Dialog>
		)
	}

	return (
		<Dialog open={open && showChangePasswordForm} maxWidth='xs' fullWidth>
			<DialogTitle>Change Password</DialogTitle>
			<DialogContent dividers>
				<Grid container spacing={1}>
					<Grid item md={12}>
						<TextField
							required
							type={showPassword.password_1 ? 'text' : 'password'}
							id='old_password'
							label='Old Password'
							value={userInfo?.old_password || ''}
							fullWidth
							margin="normal"
							error={error?.old_password ? true : false}
							helperText={error?.old_password || ''}
							onChange={(e) => onFieldChange('old_password', e.target.value)}
							onFocus={() => setError({ ...error, 'old_password': '' })}
							InputLabelProps={{ shrink: true }}
							InputProps={{
								endAdornment: <InputAdornment position="end">
									<IconButton
										onClick={(e) => console.log('')}
										onMouseDown={(e) => setShowPassword({ ...showPassword, password_1: !showPassword.password_1 })}
									>
										{showPassword.password_1 ? <Visibility /> : <VisibilityOff />}
									</IconButton>
								</InputAdornment>,
							}}
							variant='outlined'
							inputProps={{ maxLength: 100 }}
						/>
					</Grid>
					<Grid item md={12}>
						<TextField
							required
							type={showPassword.password_2 ? 'text' : 'password'}
							id='password'
							label={
								<>
									New Password
									<Tooltip placement='right' title={
										<>
											<ul className="list-unstyled">
												<li>Password Rules:</li>
												<li>- Password can contain a maximum of 20 characters</li>
												<li>- Password must contain minimum of 8 characters</li>
												<li>- Password must contain at least 1 number</li>
												<li>- Password must contain at least 1 uppercase character</li>
												<li>- Password must contain at least 1 lowercase character</li>
												<li>- Password must contain at least 1 special character</li>
											</ul>
										</>
									}
										arrow>
										<InfoIcon fontSize='small' style={{ verticalAlign: 'bottom', marginLeft: '3px' }} />
									</Tooltip>
								</>
							}
							value={userInfo?.password || ''}
							fullWidth
							margin="normal"
							error={error?.password ? true : false}
							helperText={error?.password || ''}
							onChange={(e) => onFieldChange('password', e.target.value)}
							onFocus={() => setError({ ...error, 'password': '' })}
							InputLabelProps={{ shrink: true, style: { pointerEvents: 'auto' } }}
							InputProps={{
								endAdornment: <InputAdornment position="end">
									<IconButton
										onMouseDown={(e) => setShowPassword({ ...showPassword, password_2: !showPassword.password_2 })}
									>
										{showPassword.password_2 ? <Visibility /> : <VisibilityOff />}
									</IconButton>
								</InputAdornment>,
							}}
							variant='outlined'
							inputProps={{ maxLength: 20 }}
						/>
					</Grid>
					<Grid item md={12}>
						<TextField
							required
							type={showPassword.password_3 ? 'text' : 'password'}
							id='confirm_password'
							label='Confirm Password'
							value={userInfo?.confirm_password || ''}
							fullWidth
							margin="normal"
							error={error?.confirm_password ? true : false}
							helperText={error?.confirm_password || ''}
							onChange={(e) => onFieldChange('confirm_password', e.target.value)}
							onFocus={() => setError({ ...error, 'confirm_password': '' })}
							InputLabelProps={{ shrink: true }}
							InputProps={{
								endAdornment: <InputAdornment position="end">
									<IconButton
										onMouseDown={(e) => setShowPassword({ ...showPassword, password_3: !showPassword.password_3 })}
									>
										{showPassword.password_3 ? <Visibility /> : <VisibilityOff />}
									</IconButton>
								</InputAdornment>,
								maxLength: 20
							}}
							variant='outlined'
							inputProps={{ maxLength: 20 }}
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isLoading}>Cancel</Button>
				<Button disabled={isLoading} onClick={onChangePwd} color='primary' variant='contained'>
					{isLoading ? <CircularProgress size={20} /> : 'Save'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}

const mapStateToProps = state => ({
	open: state.shareReducer.forceChangePasswordModal,
	responseData: state.shareReducer.forceChangePasswordModalData,
});

const mapDispatchToProps = (dispatch) => {
	return {
		onClose: () => {
			resetPasswordExpiredToast();
			dispatch({
				type: HIDE_FORCE_CHANGE_PASSWORD_MODAL,
			})
		}
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(ChangePasswordPopUp);
